// ---------------------------------------------------------------------------
// Hex board geometry — visual-only flat-top hex rendering of the 21×15 Grid
// ---------------------------------------------------------------------------
//
// The hex board (HexBoard.js, `?hex=1`) draws the SAME 315-subplot Grids the
// square board draws — no data changes. Each flat subplot index s maps to one
// flat-top hexagon; this module owns every coordinate conversion so the
// component only deals in subplot indices and pixels.
//
// Layout: the two Grids are EXACT MIRRORS across the divider. It's PvP — what
// you see as your own Grid must be, cell for cell and shape for shape, what
// the opponent sees as the enemy Grid on their screen — so each half is its
// own odd-q lattice with the odd columns shoved half a hex AWAY from the
// divider. (An interlocking single lattice was tried and rejected: it made
// the two halves geometrically different.)
//
//   self Grid  below the divider:  sr 0 touches the line, sr grows downward
//   opp  Grid  above the divider:  sr 0 touches the line, sr grows upward
//   sc runs left→right on both halves, exactly as on the square board.
//
// Between the halves runs the BAND: a slim strip of background replacing the
// square board's 3px yellow line — no corridor badges (the numbered hex
// badges that first lived here were dropped; the band kept half their
// height). The even columns' sr-0 hexes meet the band's edges flat; the odd
// columns sit half a hex further back on BOTH sides, so the band has a notch
// per odd column.
//
// Flat-top hex, circumradius R (center → corner):
//   width 2R, height H = √3·R, column pitch 1.5R, row pitch H.
//   half-local center(sc, sr) = ( R + 1.5R·sc ,  H/2 + H·sr + (sc odd ? H/2 : 0) )
//   measured from the half's band-side edge outward.
//   21 columns fill  2R + 20·1.5R = 32R  of width;
//   each half is     15H + H/2 = 15.5H  tall (the odd-column overhang),
//   the band is      (0.75H + BAND_PAD) / 2  (half the old badge band),
//   so the board is  31H + that  tall with the band's centre (D) at exactly 50%.
//
// Axial coordinates (q, r) follow Red Blob Games' flat-top convention; the
// odd-q offset conversion is the standard one. Pixel → hex mirrors the point
// into the half's local lattice, then uses the fractional axial inverse +
// cube rounding, so a tap lands on the hex whose center is nearest — never a
// bounding-box guess.

import { SUB_COLS, SUB_ROWS, subIndex, subCoords } from '../rules/subplotGrid'

export const HEX_COLS = SUB_COLS           // 21
export const HEX_ROWS = SUB_ROWS           // 15 per half
export const SQRT3    = Math.sqrt(3)

// The board fills the square board's width: 21 flat-top columns span 32R.
export function radiusForWidth(width) {
    return width / 32
}

export function hexHeight(R) {
    return SQRT3 * R
}

// Each half is 15.5 hexes tall (15 rows + the odd-column half-step).
export function halfHeight(R) {
    return (HEX_ROWS + 0.5) * hexHeight(R)
}

// The band once held ¾-cell hex badges with BAND_PAD of clearance; it is now
// half that tall. BAND_PAD sizes it to just hold the one-line instruction bar
// (HexBoard's prompt sits exactly in the band, over neither half): about
// 14px at phone width.
export const BAND_PAD = 13

export function bandHeight(R) {
    return (0.75 * hexHeight(R) + BAND_PAD) / 2
}

// The band's centre line — the board's exact midpoint (the old divider's y).
export function dividerY(R) {
    return halfHeight(R) + bandHeight(R) / 2
}

// The y of a half's band-side edge: where its even columns' sr-0 hexes begin.
export function halfEdgeY(side, R) {
    const g = bandHeight(R) / 2
    return side === 'self' ? dividerY(R) + g : dividerY(R) - g
}

export function boardSize(R) {
    return { width: 32 * R, height: 2 * halfHeight(R) + bandHeight(R) }
}

// ── Offset ⇄ axial (within one half's lattice) ──────────────────────────────
// Offset = (sc, sr) in the odd-q lattice, sr measured away from the divider.

export function offsetToAxial(sc, sr) {
    return { q: sc, r: sr - (sc - (sc & 1)) / 2 }
}

export function axialToOffset(q, r) {
    return { sc: q, sr: r + (q - (q & 1)) / 2 }
}

// ── Hex ⇄ pixel ─────────────────────────────────────────────────────────────
// Pixels are in board space: (0, 0) at the board's top-left corner.

// Distance of a hex's center from its half's band-side edge, and its x.
function localCenter(sc, sr, R) {
    const H = hexHeight(R)
    return { x: R + 1.5 * R * sc, d: H / 2 + H * sr + (sc & 1 ? H / 2 : 0) }
}

// Half-local distance → board y.
const toBoardY = (side, d, R) => (side === 'self' ? halfEdgeY(side, R) + d : halfEdgeY(side, R) - d)

export function hexCenter(side, sc, sr, R) {
    const { x, d } = localCenter(sc, sr, R)
    return { x, y: toBoardY(side, d, R) }
}

// Center of the hex holding subplot s on the given half.
export function subCenter(side, s, R) {
    const { sc, sr } = subCoords(s)
    return hexCenter(side, sc, sr, R)
}

// The six corners of a flat-top hex, clockwise from the right-hand corner.
export function hexCorners(cx, cy, R) {
    const H = hexHeight(R)
    return [
        [cx + R,     cy],
        [cx + R / 2, cy + H / 2],
        [cx - R / 2, cy + H / 2],
        [cx - R,     cy],
        [cx - R / 2, cy - H / 2],
        [cx + R / 2, cy - H / 2],
    ]
}

// SVG `points` attribute for the hex at (cx, cy).
export function hexPoints(cx, cy, R) {
    return hexCorners(cx, cy, R).map(([x, y]) => `${x},${y}`).join(' ')
}

// Cube-round a fractional axial coordinate to the nearest hex.
function axialRound(qf, rf) {
    const sf = -qf - rf
    let q = Math.round(qf), r = Math.round(rf), s = Math.round(sf)
    const dq = Math.abs(q - qf), dr = Math.abs(r - rf), ds = Math.abs(s - sf)
    if (dq > dr && dq > ds)      q = -r - s
    else if (dr > ds)            r = -q - s
    return { q, r }
}

// Board pixel → the hex under it as { side, s, sc, sr }, or null when the
// point is off the field (in the band, in a notch, or past the outer edges).
export function pixelToSub(x, y, R) {
    const H = hexHeight(R)
    const side = y < dividerY(R) ? 'opp' : 'self'
    const edge = halfEdgeY(side, R)
    const d = side === 'self' ? y - edge : edge - y   // distance from the half's edge
    if (d < 0) return null                            // inside the band
    // Axial (0,0) sits at localCenter(0, 0) = (R, H/2).
    const px = x - R
    const py = d - H / 2
    const qf = (2 / 3 * px) / R
    const rf = (-1 / 3 * px + SQRT3 / 3 * py) / R
    const { q, r } = axialRound(qf, rf)
    const { sc, sr } = axialToOffset(q, r)
    if (sc < 0 || sc >= HEX_COLS || sr < 0 || sr >= HEX_ROWS) return null
    return { side, sc, sr, s: subIndex(sc, sr) }
}

// ── Seams (zigzag edge paths) ───────────────────────────────────────────────

// Half-local seam along the shared edges between column `sc` and `sc + 1`,
// from the divider (d = 0) outward to the half's far edge, as [x, d] points.
// The odd column's half-step exposes one extra edge at one end: at the
// divider when the left column is odd (the right column reaches the line
// first), at the far edge when it is even (the right column overhangs).
function localColumnSeam(sc, R) {
    const H = hexHeight(R)
    const cx = R + 1.5 * R * sc
    const odd = sc & 1
    const pts = []
    if (odd) pts.push([cx + R, 0])
    for (let sr = 0; sr < HEX_ROWS; sr++) {
        const cd = H / 2 + H * sr + (odd ? H / 2 : 0)
        if (sr === 0) pts.push([cx + R / 2, cd - H / 2])
        pts.push([cx + R,     cd])
        pts.push([cx + R / 2, cd + H / 2])
    }
    if (!odd) pts.push([cx + R, halfHeight(R)])
    return pts
}

// The corridor boundary between column `sc` and `sc + 1`: the opp half's
// seam mirrored above the band, joined straight across the band (where it
// is invisible against the background) to the self half's seam below it —
// one continuous zigzag, top of the board to the bottom.
export function columnSeamPoints(sc, R) {
    const local = localColumnSeam(sc, R)
    const opp  = [...local].reverse().map(([x, d]) => [x, toBoardY('opp', d, R)])
    const self = local.map(([x, d]) => [x, toBoardY('self', d, R)])
    return [...opp, ...self]
}

// The zigzag along the shared edges between subplot rows `srAbove` and
// `srAbove + 1` on one half, across columns c0..c1 inclusive: each column's
// flat edge, joined by the slanted edge into the next column.
export function rowSeamPoints(side, srAbove, c0, c1, R) {
    const H = hexHeight(R)
    const pts = []
    for (let sc = c0; sc <= c1; sc++) {
        const cx = R + 1.5 * R * sc
        const d  = H * (srAbove + 1) + (sc & 1 ? H / 2 : 0)
        const y  = toBoardY(side, d, R)
        pts.push([cx - R / 2, y])
        pts.push([cx + R / 2, y])
    }
    return pts
}

export function pointsAttr(pts) {
    return pts.map(([x, y]) => `${x},${y}`).join(' ')
}
